'use client';

import { useMemo } from 'react';

import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent
} from '@/components/ui/chart';

import {
  CartesianGrid,
  Cell,
  Legend,
  Scatter,
  ScatterChart,
  XAxis,
  YAxis,
  ZAxis
} from 'recharts';

interface BioChartProps {
  data: any[] | string;
  config: {
    type: 'volcano' | 'pca';
    xAxisKey?: string;
    yAxisKey?: string;
    labelKey?: string;
    height?: number;
    title?: string;
    description?: string;
    significanceThreshold?: number;
    foldChangeThreshold?: number;
  };
}

export function BioChart({ data: rawData, config: rawConfig }: BioChartProps) {
  const AnyTooltipContent: any = ChartTooltipContent;
  const data = useMemo(() => {
    return typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
  }, [rawData]);

  const config = useMemo(() => {
    return typeof rawConfig === 'string' ? JSON.parse(rawConfig) : rawConfig;
  }, [rawConfig]);

  const {
    type = 'volcano',
    xAxisKey = 'log2FoldChange',
    yAxisKey = 'pvalue',
    labelKey = 'gene',
    height = 400,
    title,
    description,
    significanceThreshold = 0.05,
    foldChangeThreshold = 1
  } = config;

  // Process data for Volcano plot (p-value to -log10)
  const processedData = useMemo(() => {
    if (type === 'volcano') {
      return data.map((d: any) => ({
        ...d,
        negLogP: -Math.log10(d[yAxisKey]),
        isSignificant:
          d[yAxisKey] < significanceThreshold &&
          Math.abs(d[xAxisKey]) > foldChangeThreshold,
        direction: d[xAxisKey] > 0 ? 'up' : 'down'
      }));
    }
    return data;
  }, [
    data,
    type,
    xAxisKey,
    yAxisKey,
    significanceThreshold,
    foldChangeThreshold
  ]);

  const chartConfig = {
    up: { label: 'Upregulated', color: '#8b5cf6' },
    down: { label: 'Downregulated', color: '#3b82f6' },
    ns: { label: 'Not Significant', color: '#94a3b8' }
  };

  const xLabel = type === 'volcano' ? 'log2 Fold Change' : xAxisKey || 'PC1';
  const yLabel = type === 'volcano' ? '-log10(p-value)' : yAxisKey || 'PC2';

  return (
    <div className='border-border bg-card text-muted-foreground my-8 w-full overflow-hidden rounded-xl border p-6 shadow-sm'>
      {(title || description) && (
        <div className='mb-6'>
          {title && (
            <h4 className='text-foreground font-serif text-xl font-bold'>
              {title}
            </h4>
          )}
          {description && (
            <p className='text-muted-foreground mt-1 text-sm'>{description}</p>
          )}
        </div>
      )}

      <div
        style={{ height: height, minHeight: height }}
        className='mt-4 w-full'>
        <ChartContainer
          config={chartConfig}
          className='h-full w-full'>
          <ScatterChart
            margin={{
              top: 20,
              right: 20,
              bottom: 20,
              left: 20
            }}>
            <CartesianGrid
              strokeDasharray='3 3'
              stroke='currentColor'
              strokeOpacity={0.12}
              vertical={false}
            />
            <XAxis
              type='number'
              dataKey={type === 'volcano' ? xAxisKey : xAxisKey}
              name={xLabel}
              stroke='currentColor'
              strokeOpacity={0.4}
              fontSize={12}
              tickLine={false}
              axisLine={false}
              label={{
                value: xLabel,
                position: 'bottom',
                fill: 'currentColor',
                fontSize: 12
              }}
            />
            <YAxis
              type='number'
              dataKey={type === 'volcano' ? 'negLogP' : yAxisKey}
              name={yLabel}
              stroke='currentColor'
              strokeOpacity={0.4}
              fontSize={12}
              tickLine={false}
              axisLine={false}
              label={{
                value: yLabel,
                angle: -90,
                position: 'left',
                fill: 'currentColor',
                fontSize: 12
              }}
            />
            <ZAxis
              type='category'
              dataKey={labelKey}
              name='Identifier'
            />
            <ChartTooltip content={<AnyTooltipContent />} />
            <Legend
              verticalAlign='top'
              height={36}
            />
            <Scatter
              name='Data Points'
              data={processedData}>
              {processedData.map((entry: any, index: number) => {
                let color = '#94a3b8'; // ns
                if (type === 'volcano') {
                  if (entry.isSignificant) {
                    color = entry.direction === 'up' ? '#8b5cf6' : '#3b82f6';
                  }
                } else if (type === 'pca') {
                  color = entry.color || '#10b981';
                }
                return (
                  <Cell
                    key={`cell-${index}`}
                    fill={color}
                  />
                );
              })}
            </Scatter>
          </ScatterChart>
        </ChartContainer>
      </div>
    </div>
  );
}
